// Token helpers for the auth flow. Kept separate from the storage/logout side
// effects in `index.js` so they stay pure and unit-testable.

// Decodes a JWT payload (the middle segment) into an object. Fails closed to `{}`
// for anything that is not a well-formed, base64url-decodable, JSON payload, so
// callers never throw on a malformed or non-JWT token.
export const decodeTokenPayload = (token) => {
  if (typeof token !== 'string') {
    return {};
  }

  const segments = token.split('.');
  if (segments.length !== 3) {
    return {};
  }

  // base64url -> base64, then pad to a multiple of 4.
  let base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }

  try {
    // atob yields a "binary string" (one byte per char); reinterpreting those
    // bytes as UTF-8 keeps multi-byte claims (accents, non-Latin scripts) intact.
    // Plain JSON.parse(atob(...)) would corrupt them. `exp` (the only claim this
    // file reads) is ASCII, but the helper is exported for reading text claims too.
    return JSON.parse(utf8FromBinaryString(atob(base64)));
  } catch {
    return {};
  }
};

// Reinterprets a base64-decoded "binary string" as UTF-8 text. Uses TextDecoder
// when available (the browser runtime), and falls back to the decodeURIComponent
// idiom otherwise (e.g. jsdom, where TextDecoder is not a global) — both yield
// the same UTF-8-correct result, so callers get identical behavior everywhere.
const utf8FromBinaryString = (binary) => {
  if (typeof TextDecoder !== 'undefined') {
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }
  return decodeURIComponent(
    Array.from(binary, (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
  );
};

// Whether a JWT is expired according to its `exp` claim (seconds since epoch).
// A token with no readable `exp` is treated as NOT expired: `exp` is the single
// source of truth for expiry, and failing closed here would force a login on
// every non-standard token. The `skewSeconds` leeway (default 0) lets callers
// treat a token as expired slightly early, avoiding a request that races the
// real expiry and comes back 401.
export const isTokenExpired = (token, { skewSeconds = 0, nowMs = Date.now() } = {}) => {
  const { exp } = decodeTokenPayload(token);
  if (typeof exp !== 'number') {
    return false;
  }
  return exp * 1000 <= nowMs + skewSeconds * 1000;
};
